import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ErrorBoundary } from 'react-error-boundary';
import { store } from '@store/store';
import { theme } from '@styles/theme';
import { ProtectedRoute } from '@features/auth/ProtectedRoute';
import { MainLayout } from '@components/common/MainLayout';
import 'react-toastify/dist/ReactToastify.css';

// Lazy load components for code splitting
const Login = lazy(() => import('@features/auth/Login').then(m => ({ default: m.Login })));
const AdminLogin = lazy(() => import('@features/auth/AdminLogin').then(m => ({ default: m.AdminLogin })));
const Dashboard = lazy(() => import('@features/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const EC2List = lazy(() => import('@features/ec2/EC2List').then(m => ({ default: m.EC2List })));
const EC2Details = lazy(() => import('@features/ec2/EC2Details').then(m => ({ default: m.EC2Details })));
const EC2Metrics = lazy(() => import('@features/ec2/EC2Metrics').then(m => ({ default: m.EC2Metrics })));
const RDSList = lazy(() => import('@features/rds/RDSList').then(m => ({ default: m.RDSList })));
const RDSDetails = lazy(() => import('@features/rds/RDSDetails').then(m => ({ default: m.RDSDetails })));
const AlertRules = lazy(() => import('@features/alerts/AlertRules').then(m => ({ default: m.AlertRules })));
const Unauthorized = lazy(() => import('@features/auth/Unauthorized').then(m => ({ default: m.Unauthorized })));
const S3List = lazy(() => import('@features/s3/S3List').then(m => ({ default: m.S3List })));
const S3Details = lazy(() => import('@features/s3/S3Details').then(m => ({ default: m.S3Details })));
const URLList = lazy(() => import('@features/urls/URLList').then(m => ({ default: m.URLList })));
const URLAdd = lazy(() => import('@features/urls/URLAdd').then(m => ({ default: m.URLAdd })));
const URLDetails = lazy(() => import('@features/urls/URLDetails').then(m => ({ default: m.URLDetails })));
const S3RestoreMain = lazy(() => import('@features/s3Restore/S3RestoreMain').then(module => ({ default: module.S3RestoreMain })));
const WebhookSettings = lazy(() => import('@features/settings/WebhookSettings').then(m => ({ default: m.WebhookSettings })));
const AccessControl = lazy(() => import('@features/settings/AccessControl').then(m => ({ default: m.AccessControl })));
const DimsComputerList = lazy(() => import('@features/dims/DimsComputerList').then(m => ({ default: m.DimsComputerList })));
const DimsDbMonitor = lazy(() => import('@features/dims-db-monitor/DimsDbMonitor').then(m => ({ default: m.DimsDbMonitor })));
const DimsAlerts = lazy(() => import('@features/dims-alerts/DimsAlerts').then(m => ({ default: m.DimsAlerts })));
const BillingPage = lazy(() => import('@features/settings/BillingSettings').then(m => ({ default: m.BillingSettings })));
// BOTSCAN-DISABLED-APPINFRA: component file was never created, breaking `vite build`.
// const ApplicationInfrastructure = lazy(() => import('@features/appInfrastructure/ApplicationInfrastructure').then(m => ({ default: m.ApplicationInfrastructure })));
// BOTSCAN-DISABLED-APPINFRA: component file was never created, breaking `vite build`.
// const ProductDetail = lazy(() => import('@features/appInfrastructure/ProductDetail').then(m => ({ default: m.ProductDetail })));

// Application Performance Monitoring. Self-contained: it has its own picker, so
// it does not depend on the Application Infrastructure pages that are still
// unwritten above.
const ApmApplications = lazy(() => import('@features/apm/ApmApplications').then(m => ({ default: m.ApmApplications })));
const ApmDashboard = lazy(() => import('@features/apm/ApmDashboard').then(m => ({ default: m.ApmDashboard })));
const ApmAccess = lazy(() => import('@features/apm/ApmAccess').then(m => ({ default: m.ApmAccess })));
const BotScan = lazy(() => import('@features/botscan/BotScan').then(m => ({ default: m.BotScan })));
const RdpAccess = lazy(() => import('@features/rdpAccess/RdpAccess').then(m => ({ default: m.RdpAccess })));
const Deployment = lazy(() => import('@features/deployment/Deployment').then(m => ({ default: m.Deployment })));
const CostDigest = lazy(() => import('@features/digest/CostDigest').then(m => ({ default: m.CostDigest })));
const UserOffboarding = lazy(() => import('@features/offboarding/UserOffboarding').then(m => ({ default: m.UserOffboarding })));
const Documentation = lazy(() => import('@features/docs/Documentation').then(m => ({ default: m.Documentation })));
const SecureVault = lazy(() => import('@features/vault/Vault').then(m => ({ default: m.Vault })));
const SecureSharePage = lazy(() => import('@features/vault/SecureSharePage').then(m => ({ default: m.SecureSharePage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const ErrorFallback: React.FC<{ error: Error; resetErrorBoundary: () => void }> = ({ 
  error, 
  resetErrorBoundary 
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      p: 3,
    }}
  >
    <h2>Something went wrong</h2>
    <pre style={{ color: 'red' }}>{error.message}</pre>
    <button onClick={resetErrorBoundary}>Try again</button>
  </Box>
);

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <CssBaseline />
              <BrowserRouter>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/admin-login" element={<AdminLogin />} />
                    {/* Secure link for an external recipient: public, no login (key in #fragment + emailed code) */}
                    <Route path="/secure-share/:shareId" element={<SecureSharePage />} />
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <MainLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      <Route path="dashboard" element={<Dashboard />} />
                      
                      {/* EC2 Routes */}
                      <Route path="ec2">
                        <Route index element={<EC2List />} />
                        <Route path=":instanceId" element={<EC2Details />} />
                        <Route path=":instanceId/metrics" element={<EC2Metrics />} />
                        <Route path="metrics" element={<EC2Metrics />} />
                      </Route>
                      
                      {/* RDS Routes */}
                      <Route path="rds">
                        <Route index element={<RDSList />} />
                        <Route path=":dbInstanceId" element={<RDSDetails />} />
                      </Route>
                      
                      {/* S3 Routes */}
                      <Route path="s3">
                        <Route index element={<S3List />} />
                        <Route path=":bucketName" element={<S3Details />} />
                      </Route>
                      
                      {/* S3 Restore Routes */}
                      <Route path="s3-restore" element={<S3RestoreMain />} />
                      
	                      {/* URL Monitoring Routes */}
	                      <Route path="urls">
	                        <Route index element={<URLList />} />
	                        <Route path="add" element={<URLAdd />} />
	                        <Route path=":urlId" element={<URLDetails />} />
	                      </Route>
                      
                      {/* BOTSCAN-DISABLED-APPINFRA: routes removed until
                          ApplicationInfrastructure.tsx and ProductDetail.tsx exist.
                          Restore from App.tsx.appinfra.bak once they are written. */}

                      {/* Application Performance Monitoring */}
                      <Route path="apm">
                        <Route
                          index
                          element={
                            <ProtectedRoute requiredPermission="apm:read">
                              <ApmApplications />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="access"
                          element={
                            <ProtectedRoute requiredPermission="apm:grant">
                              <ApmAccess />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path=":productId"
                          element={
                            <ProtectedRoute requiredPermission="apm:read">
                              <ApmDashboard />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* Alert Configuration (admins only) */}
                      {/* Bot Scan (botscan:read gate; block/delete gated by botscan:admin inside) */}
                      <Route path="botscan" element={
                        <ProtectedRoute requiredPermission="botscan:read">
                          <BotScan />
                        </ProtectedRoute>
                      } />

                      {/* RDP Access Management (rdp:read gate; actions gated per-permission inside) */}
                      <Route path="rdp-access" element={
                        <ProtectedRoute requiredPermission="rdp:read">
                          <RdpAccess />
                        </ProtectedRoute>
                      } />

                      {/* Secure Vault (vault:use gate; admin tab gated by vault:admin inside) */}
                      <Route path="vault" element={
                        <ProtectedRoute requiredPermission="vault:use">
                          <SecureVault />
                        </ProtectedRoute>
                      } />

                      {/* DIMS Production Deployment (deploy:read gate; the deploy
                          and rollback buttons are gated by deploy:execute inside) */}
                      <Route path="deployment" element={
                        <ProtectedRoute requiredPermission="deploy:read">
                          <Deployment />
                        </ProtectedRoute>
                      } />

                      {/* Weekly per-team Cost Digest (digest:read to view; the
                          save/send actions are gated by digest:manage inside) */}
                      <Route path="digest" element={
                        <ProtectedRoute requiredPermission="digest:read">
                          <CostDigest />
                        </ProtectedRoute>
                      } />

                      {/* AWS User Offboarding (offboard:read to view the IAM
                          user list + audit; disable/delete gated by
                          offboard:execute inside). Both seeded to admins only. */}
                      <Route path="offboarding" element={
                        <ProtectedRoute requiredPermission="offboard:read">
                          <UserOffboarding />
                        </ProtectedRoute>
                      } />

                      {/* Private technical doc viewer (auth-only route; the PDF
                          endpoint itself is email-allowlist gated server-side). */}
                      <Route path="docs" element={
                        <ProtectedRoute>
                          <Documentation />
                        </ProtectedRoute>
                      } />

                      <Route path="alerts" element={
                        <ProtectedRoute requiredPermission="system:admin">
                          <AlertRules />
                        </ProtectedRoute>
                      } />

                      {/* Landing spot for ProtectedRoute permission denials */}
                      <Route path="unauthorized" element={<Unauthorized />} />

                      {/* DIMS LIS Computers */}
                      <Route path="dims" element={<DimsComputerList />} />

                      {/* DIMS DB Monitor */}
                      <Route path="dims-db-monitor" element={<DimsDbMonitor />} />

                      {/* DIMS DB Alerts: its own access (dimsalerts:read), handed out per person
                          on the page's Access tab like Deployment; admin rights do not imply it */}
                      <Route path="dims-alerts" element={
                        <ProtectedRoute requiredPermission="dimsalerts:read">
                          <DimsAlerts />
                        </ProtectedRoute>
                      } />

                      {/* Billing (super_admin only, requires billing:read) */}
                      <Route path="billing" element={
                        <ProtectedRoute requiredPermission="billing:read">
                          <BillingPage />
                        </ProtectedRoute>
                      } />

                      {/* Settings Routes */}
                      <Route path="settings">
                        <Route index element={<Navigate to="access-control" replace />} />
                        <Route path="access-control" element={<AccessControl />} />
                        <Route path="webhooks" element={<WebhookSettings />} />
                      </Route>
                    </Route>
                  </Routes>
                </Suspense>
              </BrowserRouter>
              <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
            </LocalizationProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
